// models/FormPhotos/FormPhotos.js
// The data model for FormPhotos - used across all form types (DEL, Safety Audit, Near Miss, etc.)
// This class handles both uploaded photos (with presigned URLs) and pending uploads (with local file data)

import { v4 as uuidv4 } from "uuid";

export class FormPhotos {
  constructor(data = {}) {
    // Core identifiers
    this.id = data.id || uuidv4();
    this.formId = data.form_id || null;
    this.formType = data.form_type || null;

    // S3 data
    this.s3Key = data.s3_key || null;
    this.presignedUrl = data.presigned_url || null;

    // Photo metadata
    this.caption = data.caption || "";
    this.mimeType = data.mime_type || null;
    this.size = data.size || null;
    this.width = data.width || null;
    this.height = data.height || null;
    this.createdAt = data.created_at || null;

    // Geo-metadata
    this.latitude = data.latitude ?? null;
    this.longitude = data.longitude ?? null;
    this.altitude = data.altitude ?? null;
    this.bearing = data.bearing ?? null;
    this.accuracy = data.accuracy ?? null;
    this.altitudeAccuracy = data.altitude_accuracy ?? data.altitudeAccuracy ?? null;
    this.address = data.address ?? null;
    this.capturedAt = data.captured_at ?? data.capturedAt ?? null;
    this.capturedAtLocal = data.captured_at_local ?? data.capturedAtLocal ?? null;

    // Local-only state (for pending uploads)
    this.localFile = data.localFile || null;
    this.localPreviewUrl = data.localPreviewUrl || null;
    this.uploadStatus = data.uploadStatus || "pending"; // pending, uploading, uploaded, failed
  }

  /**
   * Create a FormPhotos instance from a local file selection.
   * Extracts image dimensions by loading the image.
   * @param {File} file - The File object from input selection
   * @returns {Promise<FormPhotos>} New FormPhotos instance with local preview and dimensions
   */
  static async fromLocalFile(file) {
    const photo = new FormPhotos({
      mime_type: file.type,
      size: file.size,
    });
    photo.localFile = file;
    photo.localPreviewUrl = URL.createObjectURL(file);

    // Extract dimensions by loading the image
    if (file.type.startsWith("image/")) {
      try {
        const dimensions = await photo._extractDimensions();
        photo.width = dimensions.width;
        photo.height = dimensions.height;
      } catch (e) {
        console.warn("Could not extract image dimensions:", e);
      }
    }

    return photo;
  }

  /**
   * Create a FormPhotos instance from a camera capture with geo-metadata already attached.
   * @param {File} file - The File object (from canvas.toBlob)
   * @param {object} geoMetadata - Geo data from camera hooks
   * @returns {Promise<FormPhotos>} New FormPhotos instance with geo data
   */
  static async fromCameraCapture(file, geoMetadata) {
    const photo = await FormPhotos.fromLocalFile(file);
    if (geoMetadata) {
      photo.latitude = geoMetadata.latitude ?? null;
      photo.longitude = geoMetadata.longitude ?? null;
      photo.altitude = geoMetadata.altitude ?? null;
      photo.bearing = geoMetadata.bearing ?? null;
      photo.accuracy = geoMetadata.accuracy ?? null;
      photo.altitudeAccuracy = geoMetadata.altitudeAccuracy ?? null;
      photo.address = geoMetadata.address ?? null;
      photo.capturedAt = geoMetadata.capturedAt ?? null;
      photo.capturedAtLocal = geoMetadata.capturedAtLocal ?? null;
    }
    return photo;
  }

  /**
   * Check if this photo has geo-location data.
   * @returns {boolean} True if latitude and longitude are present
   */
  hasGeoData() {
    return this.latitude !== null && this.longitude !== null;
  }

  /**
   * Extract width and height from the local preview URL.
   * @returns {Promise<{width: number, height: number}>}
   * @private
   */
  _extractDimensions() {
    return new Promise((resolve, reject) => {
      if (!this.localPreviewUrl) {
        reject(new Error("No preview URL available"));
        return;
      }

      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };
      img.src = this.localPreviewUrl;
    });
  }

  // Updating the form id
  updateFormId(formId) {
    this.formId = formId;
  }

  // Updating the form type
  updateFormType(formType) {
    this.formType = formType;
  }

  // Updating the s3 key
  updateS3Key(s3Key) {
    this.s3Key = s3Key;
  }

  // Updating the presigned URL
  updatePresignedUrl(url) {
    this.presignedUrl = url;
  }

  // Updating the caption
  updateCaption(caption) {
    this.caption = caption;
  }

  // Updating the mime type
  updateMimeType(mimeType) {
    this.mimeType = mimeType;
  }

  // Updating the size
  updateSize(size) {
    this.size = size;
  }

  // Updating the width
  updateWidth(width) {
    this.width = width;
  }

  // Updating the height
  updateHeight(height) {
    this.height = height;
  }

  // Update the upload status
  updateUploadStatus(status) {
    this.uploadStatus = status;
  }

  /**
   * Clean up local preview URL to prevent memory leaks.
   * Call this when the photo is uploaded or removed from pending list.
   */
  revokeLocalPreview() {
    if (this.localPreviewUrl) {
      URL.revokeObjectURL(this.localPreviewUrl);
      this.localPreviewUrl = null;
    }
  }

  /**
   * Get the display URL for this photo.
   * Returns local preview URL if available, otherwise presigned URL.
   * @returns {string|null} URL to display the photo
   */
  getDisplayUrl() {
    return this.localPreviewUrl || this.presignedUrl;
  }

  /**
   * Check if this photo is pending upload.
   * @returns {boolean} True if photo has not been uploaded yet
   */
  isPending() {
    return this.uploadStatus === "pending" || this.uploadStatus === "uploading";
  }

  // To json for sending to the database
  toJson() {
    return {
      id: this.id,
      form_id: this.formId,
      form_type: this.formType,
      s3_key: this.s3Key,
      caption: this.caption,
      mime_type: this.mimeType,
      size: this.size,
      width: this.width,
      height: this.height,
      latitude: this.latitude,
      longitude: this.longitude,
      altitude: this.altitude,
      bearing: this.bearing,
      accuracy: this.accuracy,
      altitude_accuracy: this.altitudeAccuracy,
      address: this.address,
      captured_at: this.capturedAt,
      captured_at_local: this.capturedAtLocal,
    };
  }

  /**
   * Create metadata patch for add operation (after S3 upload).
   * @returns {object} Patch data for photos_metadata action: add
   */
  toAddPatch() {
    return {
      patch_type: "photos_metadata",
      action: "add",
      id: this.id,
      s3_key: this.s3Key,
      caption: this.caption,
      mime_type: this.mimeType,
      size: this.size,
      width: this.width,
      height: this.height,
      latitude: this.latitude,
      longitude: this.longitude,
      altitude: this.altitude,
      bearing: this.bearing,
      accuracy: this.accuracy,
      altitude_accuracy: this.altitudeAccuracy,
      address: this.address,
      captured_at: this.capturedAt,
      captured_at_local: this.capturedAtLocal,
    };
  }

  /**
   * Create metadata patch for caption update.
   * @returns {object} Patch data for photos_metadata action: update
   */
  toCaptionPatch() {
    return {
      patch_type: "photos_metadata",
      action: "update",
      id: this.id,
      caption: this.caption,
    };
  }

  /**
   * Create metadata patch for remove operation.
   * @returns {object} Patch data for photos_metadata action: remove
   */
  toRemovePatch() {
    return {
      patch_type: "photos_metadata",
      action: "remove",
      id: this.id,
      s3_key: this.s3Key,
    };
  }
}
